import { useMutation, useQuery } from '@apollo/client';
import { IonCol, IonGrid, IonRouterLink, IonRow, IonText, useIonToast } from '@ionic/react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useHistory } from 'react-router';

import { setMe } from '@actions/sessionActions';
import styles from '@components/Account.module.css';
import ActionIconButton from '@components/buttons/ActionIconButton';
import PrimaryButton from '@components/buttons/PrimaryButton';
import TertiaryButton from '@components/buttons/TertiaryButton';
import AccountCard from '@components/cards/AccountCard';
import ClientVerifyEmailInstructions from '@components/client/ClientVerifyEmailInstructions';
import PackagePurchaseOptionPurchase from '@components/client/PackagePurchaseOptionPurchase';
import DateStamp from '@components/dates/DateStamp';
import DeleteAccountConfirmation from '@components/DeleteAccountConfirmation';
import DeleteConfirmationAlert from '@components/DeleteConfirmationAlert';
import ToggleCheckbox from '@components/fields/ToggleCheckbox';
import AccountForm from '@components/forms/AccountForm';
import ClientPasswordUpdateForm from '@components/forms/ClientPasswordUpdateForm';
import EmailSecurityForm from '@components/forms/EmailSecurityForm';
import PackageGrantForm from '@components/forms/PackageGrantForm';
import HandleQuery from '@components/HandleQuery';
import Section from '@components/layout/Section';
import Modal, { ModalElement } from '@components/Modal';
import Nbsp from '@components/Nbsp';
import PackageGrantTable from '@components/PackageGrantTable';
import LargeText from '@components/text/LargeText';
import TwoFactorAuthentication from '@components/TwoFactorAuthentication';
import BodyCopy from '@components/typography/BodyCopy';
import BodyCopyBold from '@components/typography/BodyCopyBold';
import Heading2 from '@components/typography/Heading2';
import Hyperlink from '@components/typography/Hyperlink';
import { sessionContext } from '@context/SessionStore';
import useMutationWithValidation from '@hooks/useMutationWithValidation';
import useMyClient from '@hooks/useMyClient';
import useMyExpert from '@hooks/useMyExpert';
import useOpener from '@hooks/useOpener';
import {
  AgreementKindEnum,
  ClientAgreementToggleDocument,
  ClientAgreementToggleMutation,
  ClientAgreementToggleMutationVariables,
  ClientEraseDocument,
  ClientEraseMutation,
  ClientEraseMutationVariables,
  ClientRoleRoleEnum,
  ClientUpdateMutation,
  ExpertGlobalRoleEnum,
  GetClientWithAccountQuery,
  GetPackagePurchaseOptionsDocument,
  GetPackagePurchaseOptionsQuery,
  GetPackagePurchaseOptionsQueryVariables,
  PackageGrantFragment as PackageGrant,
  PhoneVerificationDestroyDocument,
  PhoneVerificationDestroyMutation,
  PhoneVerificationDestroyMutationVariables,
  WorkEmailConfirmationSendDocument,
  WorkEmailConfirmationSendMutation,
  WorkEmailConfirmationSendMutationVariables
} from '@typing/Generated';
import { IconList } from '@utils/iconUtils';
import { clickCurrentTarget } from '@utils/keyboardUtils';
import { currentPlanBalance, packageGrantDescription, sortByTimestamp } from '@utils/modelUtils';
import { isIosNativeApp } from '@utils/platformUtils';
import { timezoneAbbreviation } from '@utils/timeUtils';

type PackagePurchaseOption = GetPackagePurchaseOptionsQuery['packagePurchaseOptions'][number];

type ClientResponse = NonNullable<NonNullable<ClientUpdateMutation['clientUpdate']>['client']>;

type AccountListItemProps = {
  labelKey: string;
  large?: boolean;
  value: string;
};

const AccountListItem = ({ labelKey, large = false, value }: AccountListItemProps) => (
  <div className={styles.accountListItem}>
    <div className={`${styles.accountListLabel} ${large ? styles.largeWidth : ''}`}>
      <BodyCopy color="neutral500">
        <FormattedMessage id={labelKey} />
      </BodyCopy>
    </div>
    <div>
      <BodyCopy color="primary700">{value}</BodyCopy>
    </div>
  </div>
);

type PurchaseOptionBoxProps = {
  onClick: () => void;
  purchaseOption: PackagePurchaseOption;
  suggested?: boolean;
};

const PurchaseOptionBox = ({ onClick, purchaseOption, suggested = false }: PurchaseOptionBoxProps) => {
  const myClient = useMyClient();

  return (
    <div className={styles.purchaseOptionBoxWrapper}>
      <div className={styles.suggestedTag}>{suggested && <FormattedMessage id="pages.client.account.suggested" />}</div>
      <div className={styles.purchaseOptionPrice}>
        <FormattedNumber
          currency="USD"
          maximumFractionDigits={0}
          minimumFractionDigits={0}
          style="currency"
          value={purchaseOption.priceInCents / 100.0}
        />
      </div>
      <div className={styles.purchaseOptionHours}>
        <FormattedMessage id="pages.client.account.nHours" values={{ count: purchaseOption.minutes / 60 }} />
      </div>
      <div className={styles.purchaseOptionDescription}>
        <BodyCopy>
          <FormattedMessage id={`pages.client.account.purchaseOption${purchaseOption.minutes}.description`} />
        </BodyCopy>
      </div>
      <div className={styles.purchaseButton}>
        <PrimaryButton
          data-test="purchase-button"
          disabled={!myClient}
          onClick={onClick}
          textKey="dictionary.purchase"
        />
      </div>
    </div>
  );
};

type Props = {
  client: GetClientWithAccountQuery['client'];
  purchaseHours: boolean;
};

const Account = ({ client, purchaseHours = false }: Props) => {
  const intl = useIntl();
  const { dispatch } = useContext(sessionContext);
  const myExpert = useMyExpert();

  const accountFormModalRef = useRef<ModalElement>(null);
  const purchaseHoursRef = useRef<HTMLDivElement>(null);
  const passwordUpdateModalRef = useRef<ModalElement>(null);
  const myClient = useMyClient();
  const compHoursOpener = useOpener();

  const [deleteTwoFactorVerification] = useMutationWithValidation<
    PhoneVerificationDestroyMutation,
    PhoneVerificationDestroyMutationVariables
  >(PhoneVerificationDestroyDocument, 'phoneNumber');

  const [present, dismiss] = useIonToast();

  useEffect(() => {
    if (purchaseHours && !isIosNativeApp()) {
      const scrollTimeout = setTimeout(() => purchaseHoursRef.current?.scrollIntoView({ behavior: 'smooth' }), 500);

      return () => clearTimeout(scrollTimeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseHours]);

  const [clientAgreementToggle] = useMutationWithValidation<
    ClientAgreementToggleMutation,
    ClientAgreementToggleMutationVariables
  >(ClientAgreementToggleDocument, 'marketing');

  const getPackagePurchaseOptionsQuery = useQuery<
    GetPackagePurchaseOptionsQuery,
    GetPackagePurchaseOptionsQueryVariables
  >(GetPackagePurchaseOptionsDocument);

  const history = useHistory();

  const [clientErase] = useMutation<ClientEraseMutation, ClientEraseMutationVariables>(ClientEraseDocument);
  const accountDeletionConfirmationOpener = useOpener();

  const handleAccountDelete = useCallback(() => {
    if (myClient) {
      clientErase().then(() => history.push('/sign-out'));
    }
  }, [clientErase, history, myClient]);

  const [currentPackagePurchaseOption, setCurrentPackagePurchaseOption] = useState<PackagePurchaseOption | undefined>();
  const [verificationSetup, setVerificationSetup] = useState(false);

  const accountOpener = useOpener();
  const packageDetailsOpener = useOpener();
  const passwordOpener = useOpener();
  const verifyEmailModalOpener = useOpener();
  const deleteTwoFactorVerificationOpener = useOpener();

  const clearCurrentPackagePurchaseOption = useCallback(() => {
    setCurrentPackagePurchaseOption(undefined);
  }, []);

  const handleMarketingEmailsChange = useCallback(
    (checked: boolean) => {
      clientAgreementToggle({ enabled: checked, kind: AgreementKindEnum.marketing_agreement });
    },
    [clientAgreementToggle]
  );

  const handleConsultRemindersChange = useCallback(
    (checked: boolean) => {
      clientAgreementToggle({ enabled: checked, kind: AgreementKindEnum.consult_reminders });
    },
    [clientAgreementToggle]
  );

  const generateHandlePackagePurchaseOptionSelect = useCallback(
    (newPackagePurchaseOption: PackagePurchaseOption | undefined) => () => {
      setCurrentPackagePurchaseOption(newPackagePurchaseOption);
    },
    []
  );

  const handleUpdateEmail = useCallback(() => {
    verifyEmailModalOpener.close();
    accountOpener.open();
  }, [accountOpener, verifyEmailModalOpener]);

  const handleAccountFormError = useCallback(() => {
    if (accountFormModalRef.current) {
      accountFormModalRef.current.scrollToTop();
    }
  }, [accountFormModalRef]);

  const handleAccountFormSuccess = useCallback(
    (client: ClientResponse) => {
      accountOpener.close();
      if (client.id === myClient?.id) {
        dispatch(setMe(client));
      }
      if (!client.email.verified) {
        setTimeout(() => verifyEmailModalOpener.open(), 500);
      }
    },
    [accountOpener, dispatch, myClient?.id, verifyEmailModalOpener]
  );

  const handlePasswordFormError = useCallback(() => {
    if (accountFormModalRef.current) {
      accountFormModalRef.current.scrollToTop();
    }
  }, [accountFormModalRef]);

  const agreements = client.agreements;
  const marketingAgreements = agreements.filter(
    agreement => agreement.kind === AgreementKindEnum.marketing_agreement && !agreement.revoked
  );
  const consultReminderAgreements = agreements.filter(
    agreement => agreement.kind === AgreementKindEnum.consult_reminders && !agreement.revoked
  );

  const wantsMarketingEmails = marketingAgreements && marketingAgreements.length > 0;
  const wantsConsultReminders = consultReminderAgreements && consultReminderAgreements.length > 0;

  const handleTwoStepVerificationDelete = useCallback(() => {
    deleteTwoFactorVerification({});
  }, [deleteTwoFactorVerification]);

  const nextExpirationInfo = useMemo(() => {
    if (client.eligiblePackageGrants) {
      const expiring = client.eligiblePackageGrants.filter(grant => grant.expiresAt);
      const sorted = sortByTimestamp<PackageGrant>(expiring, packageGrant => packageGrant.expiresAt as Date);
      const nextExpiring = sorted.filter(grant => (grant.expiresAt as Date) > new Date()).reverse()[0];
      if (nextExpiring) {
        const description = packageGrantDescription({
          hasCarePlanning: nextExpiring.hasCarePlanning && !nextExpiring.carePlanningCompleted,
          hasUnlimitedMinutes: nextExpiring.hasUnlimitedMinutes,
          intl,
          minutes: nextExpiring.minutesRemaining
        });

        return (
          <>
            {description}
            {nextExpiring.expiresAt && (
              <>
                <Nbsp />
                <FormattedMessage id="dictionary.expiring" />
                <Nbsp />
                <DateStamp value={nextExpiring.expiresAt} />
              </>
            )}
          </>
        );
      }
    }
  }, [client.eligiblePackageGrants, intl]);

  const hasUnlimited = useMemo(() => {
    if (client.eligiblePackageGrants) {
      return client.eligiblePackageGrants.some(grant => !grant.expired && grant.hasUnlimitedMinutes);
    }
    return false;
  }, [client.eligiblePackageGrants]);

  const [workEmailConfirmationSend] = useMutationWithValidation<
    WorkEmailConfirmationSendMutation,
    WorkEmailConfirmationSendMutationVariables
  >(WorkEmailConfirmationSendDocument, 'email');

  const handleResentEmail = () => {
    if (!client.workEmail || !client.needsWorkEmailVerification) return;

    workEmailConfirmationSend({
      email: client.workEmail.email
    }).then(res => {
      if (res?.workEmailConfirmationSend?.errors.length === 0) {
        present({
          buttons: [{ handler: () => dismiss(), icon: IconList.xmark }],
          color: 'success',
          cssClass: 'validationToast',
          duration: 4000,
          message: intl.formatMessage({ id: 'pages.resendConfirmWorkEmail.sent' }),
          position: 'top'
        });
      }
    });
  };

  return (
    <>
      <>
        <>
          <>
            {verificationSetup ? (
              <div className={styles.fullWidth}>
                <TwoFactorAuthentication client={client} onCancel={() => setVerificationSetup(false)} settingUp />
              </div>
            ) : currentPackagePurchaseOption ? (
              <PackagePurchaseOptionPurchase
                clearCurrentPackagePurchaseOption={clearCurrentPackagePurchaseOption}
                clientId={client.id}
                packagePurchaseOption={currentPackagePurchaseOption}
              />
            ) : (
              <Section
                color="primary700"
                descriptionKey="pages.client.account.description"
                icon="user"
                titleKey="pages.client.account.accountInfo"
              >
                <div className={styles.grid}>
                  <div className={styles.column1}>
                    <AccountCard>
                      <div className={styles.spaceBetween}>
                        <Heading2>
                          <FormattedMessage id="pages.client.account.userAccount" />
                        </Heading2>
                        {myClient && (
                          <ActionIconButton
                            ariaLabelKey="accessibility.ariaLabels.edit"
                            data-test="edit-button"
                            icon="edit"
                            onClick={accountOpener.open}
                          />
                        )}
                      </div>
                      <div>
                        <AccountListItem labelKey="pages.client.account.labels.name" value={client.fullName} />
                        <AccountListItem labelKey="pages.client.account.labels.email" value={client.email.email} />
                        <AccountListItem
                          labelKey="pages.client.account.labels.phone"
                          value={client.consultPhoneNumber?.formattedNumber ?? ''}
                        />
                        <AccountListItem
                          labelKey="pages.client.account.labels.timezone"
                          value={client.timezone ? timezoneAbbreviation(client.timezone, new Date()) ?? '' : ''}
                        />
                        <AccountListItem
                          labelKey="pages.client.account.labels.residence"
                          value={client.location.address ?? ''}
                        />
                      </div>
                      <div className={styles.marketingAgreements}>
                        <ToggleCheckbox
                          checked={wantsMarketingEmails}
                          disabled={!myClient}
                          labelKey="pages.client.account.marketingEmailsLabel"
                          name="marketingEmails"
                          onChange={handleMarketingEmailsChange}
                          tabIndex={0}
                        />
                      </div>
                      {client.consultPhoneNumber && (
                        <div className={styles.consultAgreements}>
                          <ToggleCheckbox
                            checked={wantsConsultReminders}
                            disabled={!myClient}
                            labelKey="pages.client.account.consultReminderEmailsLabel"
                            name="consultReminders"
                            onChange={handleConsultRemindersChange}
                            tabIndex={0}
                          />
                        </div>
                      )}
                      {myClient && (
                        <IonRouterLink
                          data-test="change-password"
                          onClick={passwordOpener.open}
                          onKeyUp={clickCurrentTarget}
                          tabIndex={0}
                        >
                          <strong>
                            <FormattedMessage id="pages.client.account.changePassword" />
                          </strong>
                        </IonRouterLink>
                      )}
                    </AccountCard>
                  </div>
                  <div className={styles.column2}>
                    <AccountCard>
                      <div className={styles.spaceBetween}>
                        <Heading2>
                          <FormattedMessage id="pages.client.account.privacy" />
                        </Heading2>
                      </div>
                      <div className={styles.accountListItem}>
                        <div className={styles.accountListLabel + ' ' + styles.largeWidth}>
                          <BodyCopyBold color="primary700">
                            <FormattedMessage id="pages.client.account.twoFactor.twoFactorAuthentication" />
                          </BodyCopyBold>
                        </div>
                        <div className={styles.twoFactorControl}>
                          {client.verificationPhoneNumber?.verified ? (
                            <>
                              <FormattedMessage id="pages.client.account.twoFactor.status" />
                              <IonText className={styles.enabled}>
                                <FormattedMessage id="pages.client.account.twoFactor.enabled" />
                              </IonText>
                              <div className={styles.spacing}>
                                <FormattedMessage
                                  id="pages.client.account.twoFactor.phoneNumber"
                                  values={{ number: client.verificationPhoneNumber.formattedNumber }}
                                />
                              </div>
                              <div className={styles.spacing}>
                                <IonRouterLink onClick={deleteTwoFactorVerificationOpener.open}>
                                  <FormattedMessage id="pages.client.account.twoFactor.remove" />
                                </IonRouterLink>
                              </div>
                            </>
                          ) : (
                            <div>
                              <Hyperlink onClick={() => setVerificationSetup(true)}>
                                <FormattedMessage id="pages.client.account.twoFactor.begin" />
                              </Hyperlink>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={`${styles.accountListItem} ${styles.moreBottomPadding}`}>
                        <div className={styles.accountListLabel + ' ' + styles.largeWidth}>
                          <BodyCopyBold color="primary700">
                            <FormattedMessage id="pages.client.account.emailPreferences" />
                          </BodyCopyBold>
                          <p className={styles.emailPreferencesDescription}>
                            <BodyCopy color="neutral500">
                              <FormattedMessage id="pages.client.account.emailPreferencesDescription.p1" />
                            </BodyCopy>
                          </p>
                          <p className={styles.emailPreferencesDescription}>
                            <BodyCopy color="neutral500">
                              <FormattedMessage id="pages.client.account.emailPreferencesDescription.p2" />
                            </BodyCopy>
                          </p>
                        </div>
                        <div>
                          <EmailSecurityForm client={client} />
                        </div>
                      </div>
                    </AccountCard>
                    {client.roles.some(role => role.role === ClientRoleRoleEnum.owner) && (
                      <>
                        <div className={styles.section + ' ' + styles.packageSummary}>
                          <div className={styles.sectionHeader}>
                            <Heading2>
                              <FormattedMessage id="pages.client.account.packageSummary" />
                            </Heading2>
                            {client.needsWorkEmailVerification && (
                              <PrimaryButton
                                onClick={handleResentEmail}
                                size="small"
                                textKey="models.client.verifyEmail.sendWork"
                              />
                            )}
                          </div>

                          <IonGrid className={`ion-no-padding ${styles.packageSummaryGrid} ${styles.sectionBody}`}>
                            <IonRow className={styles.packageSummaryTopLine}>
                              <IonCol>
                                <LargeText>
                                  <FormattedMessage id="pages.client.account.yourPlan" />
                                </LargeText>
                              </IonCol>
                              <IonCol size="auto">
                                <LargeText>{currentPlanBalance(client, intl)}</LargeText>
                              </IonCol>
                            </IonRow>
                            <IonRow>
                              <IonCol>
                                <TertiaryButton
                                  icon="caretDown"
                                  onClick={packageDetailsOpener.toggle}
                                  textKey="pages.client.account.viewDetails"
                                />
                              </IonCol>
                              <IonCol className={styles.nextExpiration} size="auto">
                                {nextExpirationInfo}
                              </IonCol>
                            </IonRow>
                            {packageDetailsOpener.isOpen && (
                              <IonRow>
                                <IonCol>
                                  <PackageGrantTable packageGrants={client.eligiblePackageGrants} />
                                  {myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.manager && (
                                    <div className={styles.compButtonWrapper}>
                                      {!compHoursOpener.isOpen && (
                                        <PrimaryButton onClick={compHoursOpener.open} textKey="dictionary.compHours" />
                                      )}
                                      {compHoursOpener.isOpen && (
                                        <PackageGrantForm
                                          clientId={client.id}
                                          onCancel={compHoursOpener.close}
                                          onSuccess={compHoursOpener.close}
                                        />
                                      )}
                                    </div>
                                  )}
                                </IonCol>
                              </IonRow>
                            )}
                          </IonGrid>
                        </div>
                        {!hasUnlimited && !isIosNativeApp() && (
                          <div className={styles.section} ref={purchaseHoursRef}>
                            <HandleQuery query={getPackagePurchaseOptionsQuery}>
                              {packagePurchaseOptionsData => {
                                const oneHour = packagePurchaseOptionsData.packagePurchaseOptions.find(
                                  option => option.minutes === 60
                                ) as PackagePurchaseOption;
                                const threeHour = packagePurchaseOptionsData.packagePurchaseOptions.find(
                                  option => option.minutes === 180
                                ) as PackagePurchaseOption;
                                const sevenHour = packagePurchaseOptionsData.packagePurchaseOptions.find(
                                  option => option.minutes === 420
                                ) as PackagePurchaseOption;
                                return (
                                  <>
                                    <Heading2>
                                      <FormattedMessage id="pages.client.account.extend" />
                                    </Heading2>
                                    <IonGrid className={`ion-no-padding ${styles.sectionBody}`}>
                                      <IonRow className={styles.purchaseOptionBoxRow}>
                                        <IonCol className={styles.purchaseOptionBox}>
                                          <PurchaseOptionBox
                                            onClick={generateHandlePackagePurchaseOptionSelect(oneHour)}
                                            purchaseOption={oneHour}
                                          />
                                        </IonCol>
                                        <IonCol className={`${styles.purchaseOptionBox} ${styles.suggested}`}>
                                          <PurchaseOptionBox
                                            onClick={generateHandlePackagePurchaseOptionSelect(threeHour)}
                                            purchaseOption={threeHour}
                                            suggested
                                          />
                                        </IonCol>
                                        <IonCol className={styles.purchaseOptionBox}>
                                          <PurchaseOptionBox
                                            onClick={generateHandlePackagePurchaseOptionSelect(sevenHour)}
                                            purchaseOption={sevenHour}
                                          />
                                        </IonCol>
                                      </IonRow>
                                    </IonGrid>
                                  </>
                                );
                              }}
                            </HandleQuery>
                          </div>
                        )}
                        <div className={styles.section}>
                          <Heading2 color="primary700">
                            <FormattedMessage id="pages.client.account.packageEducation.heading1" />
                          </Heading2>
                          <div className={styles.sectionBody}>
                            <BodyCopy>
                              <FormattedMessage id="pages.client.account.packageEducation.content1" />
                            </BodyCopy>
                          </div>
                        </div>
                        <div className={styles.section}>
                          <Heading2 color="primary700">
                            <FormattedMessage id="pages.client.account.packageEducation.heading2" />
                          </Heading2>
                          <div className={styles.sectionBody}>
                            <BodyCopy>
                              <FormattedMessage id="pages.client.account.packageEducation.content2" />
                            </BodyCopy>
                          </div>
                        </div>
                      </>
                    )}
                    {myClient && (
                      <AccountCard>
                        <Heading2>
                          <FormattedMessage id="pages.client.account.dangerZone" />
                        </Heading2>
                        <div className={styles.sectionBody}>
                          <div className="ion-margin-bottom">
                            <FormattedMessage id="pages.client.account.deleteMyAccountExplanation" />
                          </div>
                          <IonRouterLink
                            onClick={accountDeletionConfirmationOpener.open}
                            onKeyUp={clickCurrentTarget}
                            tabIndex={0}
                          >
                            <strong>
                              <FormattedMessage id="pages.client.account.deleteMyAccount" />
                            </strong>
                          </IonRouterLink>
                        </div>
                      </AccountCard>
                    )}
                  </div>
                </div>
              </Section>
            )}
          </>
          <Modal
            opener={passwordOpener}
            ref={passwordUpdateModalRef}
            title={intl.formatMessage({ id: 'models.client.changePassword' })}
          >
            {({ modalDisplayed }) => (
              <ClientPasswordUpdateForm
                autofocus={modalDisplayed}
                onCancel={passwordOpener.close}
                onError={handlePasswordFormError}
                onSuccess={() => {
                  passwordOpener.close();
                  close();
                }}
              />
            )}
          </Modal>
          <DeleteConfirmationAlert
            deleteKey="pages.client.account.twoFactor.delete"
            messageKey="pages.client.account.twoFactor.reallyDelete"
            onDelete={handleTwoStepVerificationDelete}
            opener={deleteTwoFactorVerificationOpener}
          />
          <Modal
            opener={accountOpener}
            ref={accountFormModalRef}
            title={intl.formatMessage({ id: 'models.client.editAccount' })}
          >
            {({ modalDisplayed }) => (
              <AccountForm
                autofocus={modalDisplayed}
                client={client}
                onCancel={accountOpener.close}
                onError={handleAccountFormError}
                onSuccess={handleAccountFormSuccess}
              />
            )}
          </Modal>
          <DeleteAccountConfirmation onDelete={handleAccountDelete} opener={accountDeletionConfirmationOpener} />
          <Modal
            dataCy="verify-email-modal"
            opener={verifyEmailModalOpener}
            title={intl.formatMessage({ id: 'models.client.verifyEmail.title' })}
          >
            {() => <ClientVerifyEmailInstructions client={client} handleUpdateEmail={handleUpdateEmail} />}
          </Modal>
        </>
      </>
    </>
  );
};

export default Account;
